import { useState, useEffect } from 'react';
import { fetchSession } from '../services/AppSessionServices';
import { ITickets } from '../pages/customerdetail/models/customerDataModels';
import { ITableDataResponse } from '../pages/preAuthenticationDetail/models/PreAuthModel';
import { getTableData } from '../services/PreAuthPageServices';

interface UseFetchSessionProps {
	storeId: string;
	sessionId: string;
	clientToken: string;
	smsCode: string;
	tableRef: string;
	newTableRef: string | null;
}

export const useFetchSession = ({
	storeId,
	sessionId,
	clientToken,
	smsCode,
	tableRef,
	newTableRef,
}: UseFetchSessionProps): {
    sessionResponse: ITickets | null;
    sessionError: boolean;
    loading: boolean;
    prevTableName: string;
    showErrorModal: boolean;
    shouldBlockNewTable: boolean;
    invalidSession: boolean;
} => {
	const [sessionResponse, setSessionResponse] = useState<ITickets | null>(null);
	const [sessionError, setSessionError] = useState(false);
	const [loading, setLoading] = useState(true);
	const [prevTableName, setPrevTableName] = useState('');
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [shouldBlockNewTable, setShouldBlockNewTable] = useState(false);
	const [invalidSession, setInvalidSession] = useState(false);

	const isPreAuth = !clientToken && !smsCode;
	const isTableChanged = tableRef && newTableRef && newTableRef !== tableRef;

	const handleTableConflict = async () => {
		const tableDataDetails: ITableDataResponse = await getTableData(storeId, tableRef);

		setPrevTableName(tableDataDetails?.table?.name || '');
		setShowErrorModal(true);
		setShouldBlockNewTable(true);

		const previousMenuPage = localStorage.getItem('menuPage') || '';
		const merchantLogo = localStorage.getItem('merchantLogo') || '';

		localStorage.setItem('storedSessionId', sessionId);
		localStorage.setItem('tableRef', tableRef);
		localStorage.setItem('menuPage', previousMenuPage);
		localStorage.setItem('merchantLogo', merchantLogo);

		if (!isPreAuth) {
			localStorage.setItem('clientToken', clientToken);
			localStorage.setItem('clientCode', smsCode);
		}

		setLoading(false);
	};

	useEffect(() => {
		const fetchData = async () => {
			let hasError = false;

			try {
				const response = await fetchSession(storeId, sessionId, clientToken, smsCode, tableRef);
				if (!response.error) {
					setSessionResponse(response);
				} else {
					hasError = true;
				}
			} catch {
				hasError = true;
			}

			setSessionError(hasError);

			if (!hasError && isTableChanged) {
				await handleTableConflict();
				return;
			}

			if (sessionId && hasError) {
				localStorage.clear();
				setInvalidSession(true);
				return;
			}

			setLoading(false);
		};

		if (storeId && sessionId && clientToken && smsCode && tableRef) {
			fetchData();
			return;
		}

		if (isPreAuth && storeId && sessionId && isTableChanged) {
			handleTableConflict();
			return;
		}

		setLoading(false);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storeId, sessionId, clientToken, smsCode, tableRef, newTableRef]);

	return {
		sessionResponse,
		sessionError,
		loading,
		prevTableName,
		showErrorModal,
		shouldBlockNewTable,
		invalidSession,
	};
};
